import { CSSResultGroup, TemplateResult, html, PropertyValues } from 'lit';
import { OutlineElement } from '../../base/outline-element/outline-element';
import { customElement, property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { classMap } from 'lit/directives/class-map.js';
import componentStyles from './tufts-container.css.lit';
import '../tufts-pulse/tufts-pulse';
import { ResizeController } from '../../controllers/resize-controller';
import { RevealTrigger } from '../../controllers/reveal-trigger';

type containerWidths = 'wide' | 'medium' | 'narrow' | 'full';

export type backgroundColor =
  | 'white'
  | 'blue'
  | 'blue_dark'
  | 'purple'
  | 'purple_dark'
  | 'green'
  | 'tint'
  | 'grey'
  | 'transparent';

export const containerBgColor: backgroundColor[] = [
  'white',
  'blue',
  'blue_dark',
  'purple',
  'purple_dark',
  'green',
  'tint',
  'grey',
  'transparent',
];
/**
 * The TuftsContainer component provides a flexible container with optional
 * decorative pulse effects. The container can have different widths, background
 * colors, and spacing options.
 *
 * @element tufts-container
 *
 * @slot - Default content slot
 * @attr top-margin - Vertical spacing from the component above
 * @attr bottom-margin - Vertical spacing to the component below
 * @attr padding - Internal spacing of the container
 * @attr pulse-orientation - Position of the decorative pulse
 * @attr justify-end - Aligns content to the end of the container
 * @attr container-width - Width variant (wide, medium, narrow, full)
 * @attr background-color - Background color of the container
 * @attr max-pulse-size - Maximum size of the pulse effect
 * @attr pulse-mobile - Controls pulse visibility on mobile devices
 * @attr pulse-color - Color scheme for the decorative pulse
 */
@customElement('tufts-container')
export class TuftsContainer extends OutlineElement {
  static styles: CSSResultGroup = [componentStyles];
  resizeController: ResizeController;
  revealTrigger: RevealTrigger;

  // The resizeController is initialized here because displayPulseWidth is not set yet when constructor runs
  connectedCallback() {
    super.connectedCallback();
    this.initializeResizeController();
  }

  /**
   * Initialize the ResizeController if pulseOrientation is defined
   * This can be called multiple times safely
   */
  initializeResizeController() {
    if (this.pulseOrientation && !this.resizeController) {
      this.resizeController = new ResizeController(this, {
        breakpoints: [this.displayPulseWidth],
      });
    }
  }

  updated(changedProperties: PropertyValues) {
    super.updated(changedProperties);

    // If any of these pulse-related properties change,
    // make sure the resizeController is initialized
    if (
      changedProperties.has('pulseOrientation') ||
      changedProperties.has('pulseColor') ||
      changedProperties.has('maxPulseSize') ||
      changedProperties.has('minPulseSize') ||
      changedProperties.has('displayPulseWidth')
    ) {
      this.initializeResizeController();
    }

    // If pulse orientation has changed and exists now
    if (changedProperties.has('pulseOrientation') && this.pulseOrientation) {
      // Force a complete update
      this.requestUpdate();
    }
  }

  /**
   * The vertical space from the component above it (using Utopia fluid space variables defined in outline.theme.css, ie. space-3xl)
   **/
  @property({
    type: String,
    reflect: true,
    attribute: 'top-margin',
  })
  topMargin: string;

  @property({
    type: String,
    reflect: true,
    attribute: 'bottom-margin',
  })
  bottomMargin: string;

  @property({
    type: String,
    reflect: true,
    attribute: 'padding',
  })
  padding = 'space-l';

  @property({
    type: String,
    reflect: true,
    attribute: 'pulse-orientation',
  })
  pulseOrientation: null | string;

  @property({
    type: String,
    reflect: true,
    attribute: 'pulse-top-alignment',
  })
  pulseTopAlignment: null | string;

  @property({
    type: Boolean,
    reflect: true,
    attribute: 'justify-end',
  })
  justifyEnd = false;

  @property({
    type: String,
    reflect: true,
    attribute: 'container-width',
  })
  containerWidth: containerWidths = 'wide';

  @property({
    type: String,
    reflect: true,
    attribute: 'background-color',
  })
  backgroundColor: backgroundColor = 'white';

  @property({
    type: String,
    reflect: true,
    attribute: 'max-pulse-size',
  })
  maxPulseSize = '650px';

  @property({
    type: String,
    reflect: true,
    attribute: 'min-pulse-size',
  })
  minPulseSize = '25rem';

  @property({
    type: String,
    reflect: true,
    attribute: 'gap',
  })
  gap = '0px';

  /**
   * Width at which the pulse is removed
   */
  @property({
    type: String,
    reflect: true,
    converter: Number,
    attribute: 'display-pulse-width',
  })
  displayPulseWidth = 1080;

  @property({
    type: String,
    reflect: true,
    attribute: 'pulse-color',
  })
  pulseColor = 'blue';

  @property({ type: String, reflect: true, attribute: 'pulse-mobile' })
  pulseMobile: null | string;

  @property({ type: String, reflect: true, attribute: 'animation-type' })
  animationType: string;

  firstUpdated() {
    if (this.animationType) {
      this.revealTrigger = new RevealTrigger(this, {
        triggerSelector: '.animation',
        revealClass: this.animationType,
      });
    }
  }

  /**
   * By default, pulse is displayed only on desktop screens when pulse-orientation is set.
   * In the case where 'pulse-mobile' is true, the pulse is displayed at all resolutions.
   */
  displayPulse() {
    if (!this.pulseOrientation) {
      return false;
    }
    // If resizeController is not defined or currentBreakpointRange is undefined, initialize it and return false
    if (
      !this.resizeController ||
      this.resizeController.currentBreakpointRange === undefined
    ) {
      this.initializeResizeController();
      return false;
    }
    if (
      this.pulseMobile === 'true' ||
      this.resizeController.currentBreakpointRange !== 0
    ) {
      return true;
    }
    return false;
  }

  pulseTemplate(): TemplateResult {
    return html`
      ${this.displayPulse()
        ? html`<tufts-pulse
            orientation="${ifDefined(this.pulseOrientation)}"
            max-size="${ifDefined(this.maxPulseSize)}"
            pulse-top-alignment="${ifDefined(this.pulseTopAlignment)}"
            pulse-color="${this.pulseColor}"
          ></tufts-pulse>`
        : null}
    `;
  }
  render(): TemplateResult {
    const containerWidth = this.containerWidth;
    let minPulseHeight = '0';

    // Check if resizeController exists before using it
    const hasValidResizeController =
      this.resizeController &&
      this.resizeController.currentBreakpointRange !== undefined;

    // Pulse should always have a min-height
    if (
      this.pulseOrientation &&
      hasValidResizeController &&
      this.resizeController.currentBreakpointRange !== 0
    ) {
      minPulseHeight = `min(${this.minPulseSize}, ${this.maxPulseSize})`;
    }

    const classes = {
      container: true,
      [containerWidth]: true,
      justifyEnd: this.justifyEnd,
      [this.backgroundColor]: this.backgroundColor,
    };

    const containerClass = {
      'container-wrapper': true,
      'animation': !!this.animationType,
    };
    return html`
      ${this.topMargin || this.bottomMargin || this.backgroundColor
        ? html` <style>
            .container-wrapper {
              background-color: var(--bg-color--${this.backgroundColor});
              padding-top: var(--${this.padding});
              padding-bottom: var(
                --tufts-container-wrapper-padding-bottom,
                var(--${this.padding})
              );
              min-height: ${minPulseHeight};
            }
            .container {
              margin-top: var(--${this.topMargin});
              margin-bottom: var(--${this.bottomMargin});
              --tufts-container-space-between-elements: ${this.gap};
            }
          </style>`
        : ''}
      <div class="${classMap(containerClass)}">
        ${this.pulseTemplate()}
        <div class="${classMap(classes)}">
          <slot></slot>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'tufts-container': TuftsContainer;
  }
}
